<script lang="ts">
  import { tick } from "svelte";
  import { autoUpdate, computePosition, flip, offset, shift } from "@floating-ui/dom";
  import type { MenuItem } from "./types";

  interface Props {
    items: MenuItem[];
    label: string;
    align?: "start" | "end";
    header?: { label: string; description?: string } | undefined;
    busy?: boolean;
  }
  const { items, label, align = "end", header, busy = false }: Props = $props();

  let open = $state(false);
  let x = $state(0);
  let y = $state(0);
  let triggerEl = $state<HTMLButtonElement | null>(null);
  let menuEl = $state<HTMLDivElement | null>(null);

  const enabledItems = $derived(items.filter((item) => !item.disabled));

  async function position(): Promise<void> {
    if (!triggerEl || !menuEl) return;
    const result = await computePosition(triggerEl, menuEl, {
      placement: align === "end" ? "bottom-end" : "bottom-start",
      strategy: "fixed",
      middleware: [offset(6), flip(), shift({ padding: 8 })]
    });
    x = result.x;
    y = result.y;
  }

  async function toggle(): Promise<void> {
    if (busy) return;
    open = !open;
    if (open) {
      await tick();
      await position();
      focusItem(0);
    }
  }

  function close(focusTrigger = true): void {
    open = false;
    if (focusTrigger) triggerEl?.focus();
  }

  function focusItem(index: number): void {
    const nodes = menuEl?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])');
    if (!nodes || !nodes.length) return;
    const clamped = (index + nodes.length) % nodes.length;
    nodes[clamped]?.focus();
  }

  function currentIndex(): number {
    const nodes = Array.from(menuEl?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])') ?? []);
    return nodes.indexOf(document.activeElement as HTMLElement);
  }

  function onMenuKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        focusItem(currentIndex() + 1);
        break;
      case "ArrowUp":
        event.preventDefault();
        focusItem(currentIndex() - 1);
        break;
      case "Home":
        event.preventDefault();
        focusItem(0);
        break;
      case "End":
        event.preventDefault();
        focusItem(enabledItems.length - 1);
        break;
      case "Escape":
        event.preventDefault();
        close();
        break;
      case "Tab":
        close(false);
        break;
    }
  }

  function onTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      void toggle();
    } else if (event.key === "Escape" && open) {
      // Focus stays on the trigger when no item is focusable (all disabled);
      // Escape must still close the menu.
      event.preventDefault();
      close();
    }
  }

  function select(item: MenuItem): void {
    close();
    void item.onSelect();
  }

  $effect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent): void {
      if (!menuEl?.contains(event.target as Node) && !triggerEl?.contains(event.target as Node)) {
        close(false);
      }
    }
    window.addEventListener("pointerdown", onPointerDown);
    // autoUpdate keeps the fixed-position menu glued to its trigger through
    // window/ancestor scrolls and resizes (row-action menus live in scrollable tables).
    const stopTracking =
      triggerEl && menuEl ? autoUpdate(triggerEl, menuEl, () => void position()) : undefined;
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      stopTracking?.();
    };
  });
</script>

<button
  bind:this={triggerEl}
  type="button"
  class="menu-trigger"
  aria-haspopup="menu"
  aria-expanded={open}
  aria-busy={busy}
  aria-label={label}
  title={label}
  disabled={busy}
  onclick={toggle}
  onkeydown={onTriggerKeydown}
>
  <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="3" cy="8" r="1.4" /><circle cx="8" cy="8" r="1.4" /><circle cx="13" cy="8" r="1.4" /></svg>
</button>

{#if open}
  <div
    bind:this={menuEl}
    class="menu"
    role="menu"
    aria-label={label}
    tabindex="-1"
    style:left="{x}px"
    style:top="{y}px"
    onkeydown={onMenuKeydown}
  >
    {#if header}
      <div class="menu-header" role="presentation">
        <span>{header.label}</span>
        {#if header.description}<span>{header.description}</span>{/if}
      </div>
    {/if}
    {#each items as item (item.id)}
      {#if item.href}
        <a
          role="menuitem"
          class="menu-item"
          class:danger={item.danger}
          class:separated={item.separatorBefore}
          href={item.href}
          target={item.external ? "_blank" : undefined}
          rel={item.external ? "noopener noreferrer" : undefined}
          tabindex="-1"
          onclick={() => close(false)}
        >
          <span>{item.label}</span>
          {#if item.description}<span class="mi-desc">{item.description}</span>{/if}
        </a>
      {:else}
        <button
          type="button"
          role="menuitem"
          class="menu-item"
          class:danger={item.danger}
          class:separated={item.separatorBefore}
          disabled={item.disabled}
          tabindex="-1"
          onclick={() => select(item)}
        >
          <span>{item.label}</span>
          {#if item.description}<span class="mi-desc">{item.description}</span>{/if}
        </button>
      {/if}
    {/each}
  </div>
{/if}

<style>
  .menu-trigger {
    width: 34px;
    height: 34px;
    display: grid;
    place-items: center;
    border-radius: 8px;
    border: 1px solid transparent;
    background: transparent;
    color: var(--muted);
    cursor: pointer;
  }
  .menu-trigger:hover {
    color: var(--heading);
    border-color: var(--line-soft);
    background: var(--bg-2);
  }
  .menu-trigger:disabled {
    opacity: 0.55;
    cursor: wait;
  }
  .menu-trigger svg {
    width: 16px;
    height: 16px;
    fill: currentColor;
  }
  .menu {
    position: fixed;
    z-index: 120;
    min-width: 200px;
    padding: 6px;
    background: var(--surface-2);
    border: 1px solid var(--line);
    border-radius: var(--radius-btn);
    box-shadow: var(--shadow-lg);
    display: flex;
    flex-direction: column;
    gap: 1px;
  }
  .menu-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    text-align: left;
    padding: 9px 12px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: var(--text);
    font-size: 14px;
    cursor: pointer;
    min-height: 40px;
  }
  .menu-header {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px 12px 10px;
    border-bottom: 1px solid var(--line-soft);
    margin-bottom: 5px;
    color: var(--heading);
    font-size: 13px;
    font-weight: 600;
  }
  .menu-header span:last-child:not(:first-child) {
    overflow: hidden;
    color: var(--text);
    font-size: 12px;
    font-weight: 400;
    opacity: 0.72;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .menu-item.separated {
    border-top: 1px solid var(--line-soft);
    border-radius: 0 0 8px 8px;
    margin-top: 5px;
    padding-top: 13px;
  }
  .menu-item:hover:not(:disabled),
  .menu-item:focus-visible {
    background: var(--accent-hover);
    color: var(--heading);
    outline: none;
  }
  .menu-item.danger {
    color: var(--err-text);
  }
  .menu-item.danger:hover:not(:disabled) {
    background: var(--err-hover);
  }
  .menu-item:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
  .mi-desc {
    font-size: 12px;
    color: var(--text);
    opacity: 0.72;
  }
</style>
